import React from "react";

import { getFeatureFlag } from "@bridgesplit/utils";
import { Error404Page, lazy } from "@bridgesplit/ui";
import { Navigate, Route, Routes as RouterRoutes } from "react-router-dom";
import {
    LOAN_SLUG,
    PORTFOLIO_OVERVIEW_SLUG,
    STRATEGY_LOANS_SLUG,
    POINTS_SLUG,
    TWITTER_SLUG,
    DISCORD_SLUG,
    MARKET_SLUG,
    LOOP_DETAIL_SLUG,
    BORROW_SLUG,
    EARN_SLUG,
    SIGNUP_SLUG,
    LEND_SLUG,
    LOOPS_SLUG,
    VAULT_MANAGER_SLUG,
    LEND_VAULT_DETAIL_SLUG,
    ECOSYSTEM_SLUG,
    LOCKBOX_SLUG,
    LOCKBOX_DETAIL_SLUG
} from "app/constants";

import { NavigateListener, AppFallback } from "./wrappers";
import { MaintenancePage } from "./wrappers/MaintenancePage";
// These barrels already call lazy() internally, so importing the named exports
// gives us LazyExoticComponents. Wrapping again would cause React error #306.
import { PortfolioOverview } from "./portfolio";
import { Rewards, DiscordCallback, TwitterCallback } from "./points";
import { ExploreLoops, ExploreBorrow, ExploreLend, ExploreEarn, ExploreEcosystem } from "./explore";
import { LoanDetail } from "./loan";
import { MarketDetail } from "./market-detail";
import { LoopDetail } from "./loop";
import { ManagerOverview } from "./manager-view";

const StrategyLoansDetail = lazy(() => import("./strategy/loans/StrategyLoansDetail"));
const VaultDetail = lazy(() => import("./vault").then((m) => ({ default: m.VaultDetail })));
const LockboxOverview = lazy(() => import("./lockbox").then((m) => ({ default: m.LockboxOverview })));
const LockboxDetail = lazy(() => import("./lockbox").then((m) => ({ default: m.LockboxDetail })));
const EarnDetail = lazy(() => import("./earn").then((m) => ({ default: m.EarnDetail })));
const SignUp = lazy(() => import("./auth/SignUp"));
const Testing = lazy(() => import("./testing"));

const AppDialogs = lazy(() => import("./dialogs"));
const GlobalDialogs = lazy(() => import("./global-dialogs"));

export default function Routes() {
    if (getFeatureFlag("currentlyDownForMaintenance")) {
        return (
            <RouterRoutes>
                <Route path="*" element={<MaintenancePage />} />
            </RouterRoutes>
        );
    }

    return (
        <React.Suspense fallback={<AppFallback />}>
            <NavigateListener />

            <AppDialogs />
            <GlobalDialogs />

            <RouterRoutes>
                <Route path={"/"} element={<Navigate to={LEND_SLUG} />} />
                <Route path={LEND_SLUG} element={<ExploreLend />} />
                <Route path={EARN_SLUG} element={<ExploreEarn />} />
                <Route path={BORROW_SLUG} element={<ExploreBorrow />} />
                <Route path={LOOPS_SLUG} element={<ExploreLoops />} />
                <Route path={SIGNUP_SLUG} element={<SignUp />} />
                <Route path={`${MARKET_SLUG}/:symbol`} element={<MarketDetail />} />
                <Route path={`${LEND_VAULT_DETAIL_SLUG}/:vaultIdentifier`} element={<VaultDetail />} />
                <Route path={`${EARN_SLUG}/:vaultIdentifier`} element={<EarnDetail />} />
                <Route path={`${LOOP_DETAIL_SLUG}/:address`} element={<LoopDetail />} />
                <Route path={PORTFOLIO_OVERVIEW_SLUG} element={<PortfolioOverview />} />
                <Route path={`${LOAN_SLUG}/:address`} element={<LoanDetail />} />
                <Route path={`${STRATEGY_LOANS_SLUG}/:address`} element={<StrategyLoansDetail />} />
                <Route path={`${VAULT_MANAGER_SLUG}/:vaultIdentifier`} element={<ManagerOverview />} />
                <Route path={`testing`} element={<Testing />} />

                {/* Rewards */}
                <Route path="/waitlist" element={<Navigate to={POINTS_SLUG} />} />

                <Route path={POINTS_SLUG} element={<Rewards />} />
                <Route path={TWITTER_SLUG} element={<TwitterCallback />} />
                <Route path={DISCORD_SLUG} element={<DiscordCallback />} />
                <Route path={ECOSYSTEM_SLUG} element={<ExploreEcosystem />} />

                {getFeatureFlag("lockbox") && (
                    <>
                        <Route path={LOCKBOX_SLUG} element={<LockboxOverview />} />
                        <Route path={`${LOCKBOX_DETAIL_SLUG}/:address`} element={<LockboxDetail />} />
                    </>
                )}

                <Route path={"*"} element={<Error404Page />} />
            </RouterRoutes>
        </React.Suspense>
    );
}
